import { generateMarketPathname } from '@chewy/market-handler';
import { KibChewyPlusLogo } from '@chewy/kib-logos-react';
import Image from 'next/image';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RotatingText } from '@/components/common/RotatingText/RotatingText';
import { PetProfileModel } from '@/generated-client/pet-profile-a';
import { useMarket } from '@/providers/MarketProvider';
import CatHeroImage from '@/images/cat-hero-image-v2.jpeg';
import DogHeroImage from '@/images/dog-hero-image-v2.jpeg';
import { getPetType, PetType } from '@/utils/pet-profiles';

import { FreeTrialButton } from '../../FreeTrialButton/FreeTrialButton';
import styles from './SignUpHeroSectionV2.module.scss';

const petHeroImages: Record<PetType, string> = {
  Dog: DogHeroImage.src,
  Cat: CatHeroImage.src,
  General: DogHeroImage.src,
};

const rotatingTextItems = ['savings', 'rewards', 'toys', 'treats'] as const;

interface Props {
  petProfiles: PetProfileModel[];
}

export default function SignUpHeroSectionV2({ petProfiles = [] }: Props) {
  const { t } = useTranslation();
  const { market } = useMarket();

  const heroImageSrc = useMemo(() => {
    const petType = getPetType(petProfiles);
    const imageSrc = petHeroImages[petType];
    return generateMarketPathname(imageSrc, market);
  }, [petProfiles, market]);

  return (
    <header className={styles.aboveTheFoldContainer}>
      <Image
        alt="pet-banner-header-image"
        className={styles.backgroundImage}
        fill={true}
        priority={true}
        src={heroImageSrc}
        unoptimized={true}
      />
      <div className={styles.textContainer}>
        <div className={styles.brandingContainer}>
          <KibChewyPlusLogo className={styles.chewyPlusImage} />
          <span className={styles.membership}>{t('loyalty.common.membership')}</span>
        </div>
        <div aria-hidden="true" className={styles.brandingText}>
          <span className={styles.brandingPre}>{t('loyalty.sign-up.v2.branding-pre')}</span>
          <span>
            <RotatingText items={rotatingTextItems} />
          </span>
          <br />
          <span>{t('loyalty.sign-up.v2.branding-post')}</span>
        </div>
        <span className={styles.visuallyHidden}>{t('loyalty.sign-up.rotating-text-a11y')}</span>
        <div className={styles.btnContainer}>
          <FreeTrialButton
            data-testid="sign-up:hero:open-confirmation-sheet-btn"
            displayText={t('loyalty.sign-up.v2.desktop-btn-label')}
          />
        </div>
      </div>
    </header>
  );
}
